import {
  createSession,
  createViewport,
  ORTHOGRAPHIC_CAMERA_DIRECTION,
} from "@shapediver/viewer";

// Variáveis Globais
window.session;
window.viewport;
window.perspectiveCamera;
window.topCamera;
window.rightCamera;
window.topCamera;
window.bottomCamera;

// --- CONSTANTES DE MAPEAMENTO (UUIDs do ShapeDiver) ---
const SD_PARAMS = {
  MAIN_MODEL: "20895505-b1ec-45d3-b6c3-65923a5defff",
  TETO: "ca0cf7c5-0bcd-4e9b-aabc-d2e3e97f9134",
  SALA_TOGGLE: "sala", // Nome lógico, não UUID
  DEPOSITO_TOGGLE: "18e8d087-131c-4c47-a6ae-bb4ed07268c2", // Toggle Geral Depósito
  DEPOSITO_VIS: "377c77ec-90e8-4a14-902b-8444535bfcb6", // Visibilidade Depósito
  QUADRO_VIS: "68060fa1-144d-46a0-9f16-1e04e78f3e78",
  QUADRO_TOGGLE: "817ceb83-0ff2-4bf5-99e0-9e28a89ac110",
  ORDEM_OBJETOS: "efe67a0a-d473-4671-ba15-d8b1d7926e76", // Ordem de renderização

  // Estruturas de Sala (Paredes/Chão)
  WALL_SALA_1: "adf9bd79-369d-45de-aa54-9435ff98483a", // Esconde/Mostra Sala 1
  WALL_SALA_2: "d2e50905-8ec8-4e2b-8a23-d3f7f4020611", // Esconde/Mostra Sala 2

  // Mobiliário Hall
  BALCAO_HALL: "3155ea16-7a8f-4d29-b2b8-97523bd51a9a",
  MESA_HALL: "682e8931-33e5-4179-8952-f9bf2e01e5c3",
  BISTRO_HALL: "3b3ea4bc-02bf-4c28-b5f2-b7da7956964d",
  ASPERSOR: "04b23cbc-80fa-42ef-854f-8f4e541e962a",

  // Mobiliário Sala 1
  MESA_S1: "78b052ec-acd6-411b-8ab8-8c37559d4956",
  FRIGOBAR_S1: "98d8dd37-c7ff-41ef-85ad-7f7ecc11c96a",
  BALCAO_S1: "c55fac73-147d-499a-9e1c-b8af716635d4",

  // Mobiliário Sala 2
  MESA_S2: "8398b103-19eb-47e7-8aab-58473fd1b331",
  FRIGOBAR_S2: "7254a11c-99ce-4986-9300-6a311fc5e830",
  BALCAO_S2: "8e0c914d-e5fd-4ade-91eb-51c10fbe266f",

  // Depósito Interno
  FRIGO_DEPOSITO: "88112e7a-4b58-4002-b760-2d8b003e2fd9",
};

// Lista de parâmetros que devem ser resetados (Zero) por padrão
const PARAMS_TO_RESET = [
  SD_PARAMS.BALCAO_HALL,
  SD_PARAMS.BISTRO_HALL,
  SD_PARAMS.MESA_HALL,
  SD_PARAMS.ORDEM_OBJETOS,
  SD_PARAMS.ASPERSOR,
  SD_PARAMS.QUADRO_VIS,
  SD_PARAMS.QUADRO_TOGGLE,
  SD_PARAMS.DEPOSITO_VIS,
  // SD_PARAMS.WALL_SALA_1,
  SD_PARAMS.MESA_S1,
  SD_PARAMS.FRIGOBAR_S1,
  SD_PARAMS.BALCAO_S1,
  // SD_PARAMS.WALL_SALA_2,
  SD_PARAMS.MESA_S2,
  SD_PARAMS.FRIGOBAR_S2,
  SD_PARAMS.BALCAO_S2,
];

/**
 * ETAPA 2: Consolidação de Parâmetros
 * Constrói o estado final dos parâmetros 3D baseado no resumo do pedido.
 * Retorna um objeto { "UUID": "Valor" }
 */
function buildInitialState(summary, mainModelValue) {
  // 1. Estado Base (Reset / Default)
  const state = {};

  // Define modelo principal
  state[SD_PARAMS.MAIN_MODEL] = mainModelValue || "0";
  state[SD_PARAMS.TETO] = "0";

  // Aplica reset nos opcionais
  PARAMS_TO_RESET.forEach((uuid) => {
    state[uuid] = "0";
  });

  state[SD_PARAMS.FRIGO_DEPOSITO] = "2";

  // [CORREÇÃO] Padrão explícito: Paredes Ocultas ("1") se nada for dito
  state[SD_PARAMS.WALL_SALA_1] = "1";
  state[SD_PARAMS.WALL_SALA_2] = "1";

  // Se não houver resumo, retorna o estado limpo (0 salas)
  if (!summary || !summary.items) {
    return state;
  }

  // 2. Processamento do Pedido
  const items = summary.items;

  // Helpers Locais
  const byPid = items.reduce((acc, it) => {
    (acc[it.product_id] ||= []).push(it);
    return acc;
  }, {});

  const qtyOf = (pid) =>
    (byPid[pid] || []).reduce(
      (s, it) => s + (parseInt(it.quantity, 10) || 0),
      0
    );

  const findCtx = (pid, ctx) =>
    (byPid[pid] || []).find((it) => it.meta && it.meta.context === ctx);

  const qtyCtx = (pid, ctx) =>
    (byPid[pid] || [])
      .filter((it) => it.meta && it.meta.context === ctx)
      .reduce((s, it) => s + (parseInt(it.quantity, 10) || 0), 0);

  // --- LÓGICA DE SALAS ---
  const SALA_IDS = [76];
  let salaCount = 0;
  for (const sid of SALA_IDS)
    salaCount = Math.max(salaCount, Math.min(2, qtyOf(sid)));

  if (salaCount === 0) {
    const hasS1 = items.some(
      (it) => it.meta?.context === "sala_1" && it.quantity > 0
    );
    const hasS2 = items.some(
      (it) => it.meta?.context === "sala_2" && it.quantity > 0
    );
    salaCount = hasS1 && hasS2 ? 2 : hasS1 || hasS2 ? 1 : 0;
  }

  // Tradução da lógica "Sala" para UUIDs
  if (salaCount === 0) {
    state[SD_PARAMS.WALL_SALA_1] = "1"; // Esconder
    state[SD_PARAMS.WALL_SALA_2] = "1"; // Esconder
  } else if (salaCount === 1) {
    state[SD_PARAMS.WALL_SALA_1] = "0"; // Mostrar
    state[SD_PARAMS.WALL_SALA_2] = "1"; // Esconder
  } else if (salaCount >= 2) {
    state[SD_PARAMS.WALL_SALA_1] = "0"; // Mostrar
    state[SD_PARAMS.WALL_SALA_2] = "0"; // Mostrar
  }

  // --- LÓGICA DE DEPÓSITO ---
  const depositoOn = qtyOf(74) > 0 || qtyOf(75) > 0;
  state[SD_PARAMS.DEPOSITO_VIS] = depositoOn ? "1" : "0";
  state[SD_PARAMS.DEPOSITO_TOGGLE] = depositoOn ? "1" : "0";

  // --- LÓGICA DE QUADRO ---
  const quadroItems = items.filter((it) => it.product_id === 85);
  const quadroOn = quadroItems.some(
    (it) => (parseInt(it.quantity, 10) || 0) > 0
  );
  const quadroVal = quadroOn ? "1" : "0";
  state[SD_PARAMS.QUADRO_VIS] = quadroVal;
  const quadroToggleItem = quadroItems.find(
    (it) =>
      it.meta &&
      it.meta.quadro_toggle !== undefined &&
      it.meta.quadro_toggle !== ""
  );
  if (quadroToggleItem) {
    state[SD_PARAMS.QUADRO_TOGGLE] = String(
      quadroToggleItem.meta.quadro_toggle
    );
  }

  // --- LÓGICA DE MOBILIÁRIO (Hall) ---
  const balcaoHallOn =
    !!findCtx(79, "hall") ||
    (qtyOf(79) > 0 && !findCtx(79, "sala_1") && !findCtx(79, "sala_2"));
  state[SD_PARAMS.BALCAO_HALL] = balcaoHallOn ? "1" : "0";

  state[SD_PARAMS.MESA_HALL] = String(qtyCtx(77, "hall"));
  state[SD_PARAMS.BISTRO_HALL] = String(qtyCtx(78, "hall"));
  state[SD_PARAMS.ASPERSOR] = String(qtyCtx(83, "hall"));

  // --- LÓGICA DE MOBILIÁRIO (Salas) ---
  state[SD_PARAMS.MESA_S1] = !!findCtx(77, "sala_1") ? "1" : "0";
  state[SD_PARAMS.MESA_S2] = !!findCtx(77, "sala_2") ? "1" : "0";

  state[SD_PARAMS.BALCAO_S1] = !!findCtx(79, "sala_1") ? "1" : "0";
  state[SD_PARAMS.BALCAO_S2] = !!findCtx(79, "sala_2") ? "1" : "0";

  const fS1 = findCtx(82, "sala_1");
  const fS2 = findCtx(82, "sala_2");
  const fDep = findCtx(82, "deposito");

  state[SD_PARAMS.FRIGOBAR_S1] = fS1 && fS1.quantity > 0 ? "1" : "0";
  state[SD_PARAMS.FRIGOBAR_S2] = fS2 && fS2.quantity > 0 ? "1" : "0";
  state[SD_PARAMS.FRIGO_DEPOSITO] = fDep && fDep.quantity > 0 ? "1" : "2";

  // --- REVESTIMENTO E COR ---
  const revestItem = items.find(
    (it) =>
      it.product_id === 86 && (it.meta?.revestimento || it.meta?.color_hex)
  );
  if (revestItem) {
    const val =
      revestItem.meta.color_hex || revestItem.meta.revestimento || "#ffffff";
    const pid =
      revestItem.meta.param_id || "22ff4752-5564-492a-ae84-1d58db987dfc";
    state[pid] = val;
  }

  // --- ORDEM DE OBJETOS ---
  const hasQuadro = state[SD_PARAMS.QUADRO_VIS] === "1";

  let ordem = [];

  // 1) Monta ordem padrão (igual estava antes)
  if (hasQuadro) ordem.push("0");
  if (salaCount > 0) ordem.push("1");
  if (salaCount >= 1) ordem.push("2");
  if (salaCount >= 2) ordem.push("3");
  if (ordem.length === 0) ordem = ["0", "1", "2", "3"];

  // 2) Correção: com 2 salas + depósito ligado,
  //    força a ordem usada no mock: quadro (0), depósito (3), salas (1)
  if (salaCount >= 2 && depositoOn) {
    ordem = [];

    if (hasQuadro) ordem.push("0"); // mantém quadro na frente se existir
    ordem.push("3"); // depósito
    ordem.push("1"); // bloco das salas/hall
  }

  state[SD_PARAMS.ORDEM_OBJETOS] = ordem.join(",");

  return state;
}

const exportButton = document.getElementById("export-image-btn");

// --- Helper para o Loader Local (Overlay no Canvas) ---
function toggleLocalLoader(show) {
  const localLoader = document.querySelector(".previewer-loader");
  if (!localLoader) return;
  if (show) {
    localLoader.classList.remove("hidden");
  } else {
    localLoader.classList.add("hidden");
  }
}

// --- Controle de Batch (Agrupamento de chamadas) ---
let __batchMode = false;
let __pendingParams = [];

function beginBatch() {
  __batchMode = true;
  __pendingParams = [];
}

async function endBatch() {
  __batchMode = false;
  try {
    toggleLocalLoader(true);
    if (typeof session !== "undefined" && session) {
      await session.customize();
    }
  } finally {
    toggleLocalLoader(false);
  }
}

// --- Feedback Visual nos Inputs (Loading State) ---
function toggleInputState(input, isLoading) {
  if (!input) return;
  const productItem = input.closest(".product-item");
  if (!productItem) return;

  if (isLoading) {
    productItem
      .querySelectorAll("input, button")
      .forEach((el) => (el.disabled = true));
    if (!productItem.querySelector(".item-loader-overlay")) {
      const loaderHTML = `
        <div class="item-loader-overlay">
            <div class="loader-progress-track">
                <div class="loader-progress-fill"></div>
            </div>
            <p class="item-loader-text">Atualizando...</p>
        </div>`;
      productItem.insertAdjacentHTML("beforeend", loaderHTML);
    }
  } else {
    productItem
      .querySelectorAll("input, button")
      .forEach((el) => (el.disabled = false));
    const overlay = productItem.querySelector(".item-loader-overlay");
    if (overlay) overlay.remove();
    if (input.type === "number") input.focus();
  }
}

// --- ETAPA 4: Função para Sincronizar UI (Inputs) Imediatamente ---
function syncDOMInputs() {
  const initialDataInput = document.getElementById("order-initial-data");

  if (!initialDataInput) {
    return;
  }

  if (!initialDataInput.value) {
    return;
  }

  try {
    const summary = JSON.parse(initialDataInput.value);

    if (!summary || !Array.isArray(summary.items)) return;

    // Ativa flag para impedir que os 'change' events disparem lógica 3D desnecessária
    window.isProgrammaticUpdate = true;

    summary.items.forEach((item) => {
      markInputsForItem(item);
    });

    // Mantém a flag ativa por um breve momento para cobrir o atraso dos eventos (setTimeout do markInputsForItem)
    setTimeout(() => {
      window.isProgrammaticUpdate = false;
    }, 200);
  } catch (e) {
    console.error("Erro na sincronia UI inicial:", e);
    window.isProgrammaticUpdate = false;
  }
}

// --- Inicialização do ShapeDiver ---
async function initialize() {
  const initialDataInput = document.getElementById("order-initial-data");
  if (!initialDataInput) {
    // console.log("ShapeDiver: Home detectada, script pausado.");
    return;
  }

  const mainProductShapeDiver = document.querySelector(
    "#main-product-shapediver"
  );
  const previewerBlock = document.querySelector(".previewer-block");
  const order = document.getElementById("order-id").value;

  if (!mainProductShapeDiver) return;

  // --- [ETAPA 1 & 2 INTEGRADA] ---
  // 1. Tenta obter o resumo do pedido IMEDIATAMENTE (Injeção PHP ou AJAX Fallback)
  let summary = null;

  if (initialDataInput && initialDataInput.value) {
    try {
      summary = JSON.parse(initialDataInput.value);
    } catch (e) {
      console.error("Erro no parse do JSON injetado:", e);
    }
  }

  // Fallback AJAX (apenas se necessário)
  if (!summary && order) {
    summary = await fetchOrderDraftSummary(order);
  }

  // 2. Constrói o estado inicial "perfeito" ANTES de criar a sessão
  const initialParams = buildInitialState(summary, mainProductShapeDiver.value);

  try {
    session = await createSession({
      id: "mySession",
      ticket:
        "b991a73b5efb3c06c541b330d54f18029a0d9c6ef3ec9c9bcf229fd88d6eeb259f299a2adb594f4e180559f279095ae95c7fc5497eb47ba53953607d8828d9d57478f88a7f16ef00c664713bbb1ea31f1e8bcaadaa204a1ffc41e8a47add6545d1d0722f8c21c5-e38e5cb6443a7dbb2039fb1daa8846dd",
      modelViewUrl: "https://sdr8euc1.eu-central-1.shapediver.com",
      // --- ETAPA 3: Inicialização Atômica ---
      initialParameterValues: initialParams,
    });
  } catch (error) {
    console.error("Erro crítico ao criar sessão:", error);
    document
      .querySelector('.modal[data-target="loader"]')
      .classList.remove("active");
    return;
  }

  await session.customize(); // Confirma a renderização inicial

  viewport = await createViewport({
    id: "myViewport",
    canvas: document.getElementById("previewer"),
  });

  perspectiveCamera = viewport.camera;
  topCamera = viewport.createOrthographicCamera();
  topCamera.direction = ORTHOGRAPHIC_CAMERA_DIRECTION.TOP;
  rightCamera = viewport.createOrthographicCamera();
  rightCamera.direction = ORTHOGRAPHIC_CAMERA_DIRECTION.RIGHT;
  frontCamera = viewport.createOrthographicCamera();
  frontCamera.direction = ORTHOGRAPHIC_CAMERA_DIRECTION.FRONT;
  bottomCamera = viewport.createOrthographicCamera();
  bottomCamera.direction = ORTHOGRAPHIC_CAMERA_DIRECTION.BOTTOM;

  if (perspectiveCamera) {
    perspectiveCamera.cubePositionRestriction = {
      min: [-Infinity, -Infinity, 0],
      max: [Infinity, Infinity, Infinity],
    };
    perspectiveCamera.rotationRestriction = {
      minAzimuthAngle: -Infinity,
      maxAzimuthAngle: Infinity,
      minPolarAngle: 0,
      maxPolarAngle: 85,
    };
  }

  viewport.assignCamera(perspectiveCamera.id);

  // 3. Segurança para Inputs (Redundância caso o syncDOMInputs tenha falhado)
  // O 3D já está carregado corretamente via initialParameterValues.
  if (summary) {
    window.isProgrammaticUpdate = true;
    try {
      await populateUIFromSummary(summary);
    } catch (err) {
      console.error("Erro ao popular UI:", err);
    } finally {
      window.isProgrammaticUpdate = false;
    }
  }

  previewerBlock.classList.remove("loading");
  document
    .querySelector('.modal[data-target="loader"]')
    .classList.remove("active");
}

window.exportView = async function (camera, filename) {
  return new Promise((resolve, reject) => {
    if (camera === perspectiveCamera) {
      viewport.assignCamera(camera.id);
      viewport.camera.position = [-322.06, -1377.0, 266.31];
      viewport.camera.target = [190.84, -100.49, 65.77];
    } else {
      viewport.assignCamera(camera.id);
    }
    viewport.update();
    setTimeout(() => {
      const canvas = document.getElementById("previewer");
      const imageURL = canvas.toDataURL("image/png");
      fetch(`https://${location.hostname}/wp-json/custom/v1/upload-image`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image: imageURL, filename: `${filename}.png` }),
      })
        .then((response) => response.json())
        .then((data) => {
          const uploadedImagesInput = document.getElementById(
            "main-product-preview-images"
          );
          if (data.success) {
            let imagesArray = [];
            if (uploadedImagesInput.value)
              imagesArray = JSON.parse(uploadedImagesInput.value);
            imagesArray.push(data.url);
            uploadedImagesInput.value = JSON.stringify(imagesArray);
            resolve(data.url);
          } else {
            reject(data);
          }
        })
        .catch((error) => {
          console.error("Erro:", error);
          reject(error);
        });
    }, 100);
  });
};

// Executa sincronia de UI assim que o DOM estiver pronto (Etapa 4)
document.addEventListener("DOMContentLoaded", syncDOMInputs);

initialize();

const inputs = document.querySelectorAll(
  '.product-optionals input[type="radio"], .product-optionals input[type="number"]'
);

function determinarOrdemObjetos() {
  const quadroInput = document.querySelector(
    'input[data-name="68060fa1-144d-46a0-9f16-1e04e78f3e78"]:checked'
  );
  const salaInput = document.querySelector('input[name="sala"]');
  const quadroAtivo = quadroInput && quadroInput.value === "1";

  let salaValor = 0;
  if (salaInput) {
    if (salaInput.type === "number") {
      salaValor = parseInt(salaInput.value) || 0;
    } else if (salaInput.type === "radio" && salaInput.checked) {
      salaValor = parseInt(salaInput.value) || 0;
    }
  }

  const depositoVisParam =
    session?.parameters?.["377c77ec-90e8-4a14-902b-8444535bfcb6"]; // DEPOSITO_VIS
  const depositoToggleParam =
    session?.parameters?.["18e8d087-131c-4c47-a6ae-bb4ed07268c2"]; // DEPOSITO_TOGGLE

  const depositoAtivo =
    (depositoVisParam && String(depositoVisParam.value) === "1") ||
    (depositoToggleParam && String(depositoToggleParam.value) === "1");

  let ordem = [];

  if (quadroAtivo) ordem.push("0");
  if (salaValor > 0) ordem.push("2");
  if (depositoAtivo) ordem.push("1");
  if (salaValor >= 1) ordem.push("3");
  if (salaValor >= 2) ordem.push("4");

  if (ordem.length === 0) ordem = ["0", "1", "4", "2", "3"];

  return ordem.join(",");
}

async function updateSessionParameter({ name, value, deposito, quadro }) {
  if (!session) return;
  if (session.parameters[name] || name === "sala") {
    if (name !== "sala") session.parameters[name].value = value;
    if (deposito)
      session.parameters["18e8d087-131c-4c47-a6ae-bb4ed07268c2"].value =
        deposito;
    if (quadro)
      session.parameters["817ceb83-0ff2-4bf5-99e0-9e28a89ac110"].value = quadro;

    if (name === "sala") {
      if (value === "0") {
        session.parameters["adf9bd79-369d-45de-aa54-9435ff98483a"].value = "1";
        session.parameters["d2e50905-8ec8-4e2b-8a23-d3f7f4020611"].value = "1";
      }
      if (value === "1") {
        session.parameters["adf9bd79-369d-45de-aa54-9435ff98483a"].value = "0";
        session.parameters["d2e50905-8ec8-4e2b-8a23-d3f7f4020611"].value = "1";
      }
      if (value === "2") {
        session.parameters["d2e50905-8ec8-4e2b-8a23-d3f7f4020611"].value = "0";
        session.parameters["adf9bd79-369d-45de-aa54-9435ff98483a"].value = "0";
      }
    }

    if (
      name === "sala" ||
      name === "68060fa1-144d-46a0-9f16-1e04e78f3e78" || // QUADRO_VIS
      name === "18e8d087-131c-4c47-a6ae-bb4ed07268c2" || // DEPOSITO_TOGGLE
      name === "377c77ec-90e8-4a14-902b-8444535bfcb6" // DEPOSITO_VIS  <<< NOVO
    ) {
      const novaOrdem = determinarOrdemObjetos();
      session.parameters["efe67a0a-d473-4671-ba15-d8b1d7926e76"].value =
        novaOrdem;
      // console.log("ordem: " + novaOrdem);
    }
    toggleLocalLoader(true);
    await session.customize();
    toggleLocalLoader(false);
  }
}

inputs.forEach((input) => {
  input.addEventListener("change", async () => {
    if (window.isProgrammaticUpdate) {
      return;
    }
    if (input.checked || input.type === "number") {
      toggleLocalLoader(true);
      toggleInputState(input, true);
      const name = input.dataset.name;
      const value = input.dataset.colorHex || input.value;
      const deposito = input.dataset.deposito || "";
      const quadro = input.dataset.quadro || "";
      try {
        await updateSessionParameter({ name, value, deposito, quadro });
      } catch (error) {
        console.error("Erro ao atualizar parâmetro:", error);
      } finally {
        toggleLocalLoader(false);
        toggleInputState(input, false);
        if (input.type === "number" && !input.disabled) input.focus();
      }
    }
  });
});

window.uploadedImages = {};
window.uploadedFilesByItem = window.uploadedFilesByItem || {};

function getUploadItemKey(productId, context) {
  return `${productId || ""}::${context || ""}`;
}

function normalizeUploadedFilesMeta(raw) {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }
  return null;
}

function normalizeTextureUrl(raw) {
  if (!raw || typeof raw !== "string") return "";
  const trimmed = raw.trim();
  if (!trimmed) return "";
  const currentProtocol = window.location.protocol || "https:";
  const currentOrigin = window.location.origin || "";
  const hasHttp = /^https?:\/\//i.test(trimmed);
  let candidate = trimmed;

  if (hasHttp && currentProtocol === "https:" && trimmed.startsWith("http://")) {
    candidate = `https://${trimmed.slice(7)}`;
  }

  try {
    return new URL(candidate).toString();
  } catch (e) {
    try {
      if (candidate.startsWith("//")) {
        return new URL(`${currentProtocol}${candidate}`).toString();
      }
      if (candidate.startsWith("/") && currentOrigin) {
        return new URL(candidate, currentOrigin).toString();
      }
      return new URL(`https://${candidate}`).toString();
    } catch (err) {
      return "";
    }
  }
}

function findParamNameForUpload(type, productId, context) {
  if (!type || !productId) return "";
  let selector = `input[type="file"][data-image-type="${type}"][data-id="${productId}"]`;
  if (context) selector += `[data-context="${context}"]`;
  const el = document.querySelector(selector);
  return el ? el.name : "";
}

async function applyUploadedFilesFromSummary(summary) {
  if (!summary || !Array.isArray(summary.items)) return;

  for (const it of summary.items) {
    const productId = it.product_id;
    const context = (it.meta && it.meta.context) || "";
    const parsed = normalizeUploadedFilesMeta(it.meta && it.meta.uploaded_files);
    if (!parsed) continue;

    const key = getUploadItemKey(productId, context);
    window.uploadedFilesByItem[key] = parsed;

    for (const [type, info] of Object.entries(parsed)) {
      let original = "";
      let preview = "";
      let param = "";

      if (typeof info === "string") {
        original = info;
      } else if (info && typeof info === "object") {
        original = info.original || "";
        preview = info.preview || "";
        param = info.param || "";
      }

      if (original && type) {
        window.uploadedImages[type] = original;
      }

      const paramName = param || findParamNameForUpload(type, productId, context);
      const value = normalizeTextureUrl(preview || original);
      if (paramName && value) {
        await updateSessionParameter({ name: paramName, value });
      }
    }
  }
}

async function persistUploadedFilesMeta(productId, context, filesMap) {
  if (!filesMap || !productId) return;
  if (typeof OrderDraftVars === "undefined") return;
  const orderId = document.getElementById("order-id")?.value || "";
  if (!orderId) return;

  const payload = new URLSearchParams();
  payload.append("action", "update_order_item");
  payload.append("nonce", OrderDraftVars.nonce || "");
  payload.append("order_id", orderId);
  payload.append("product_id", productId);
  payload.append("quantity", 1);
  payload.append("context", context || "");
  payload.append(
    "item_meta",
    JSON.stringify({ uploaded_files: JSON.stringify(filesMap) })
  );

  try {
    const resp = await fetch(OrderDraftVars.ajax_url, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: payload.toString(),
    });
    const json = await resp.json();
    if (json && json.success && json.data && json.data.summary) {
      const summary = json.data.summary;
      if (summary && Array.isArray(summary.items)) {
        summary.items.forEach((it) => {
          if (
            String(it.product_id) === String(productId) &&
            (it.meta?.context || "") === (context || "")
          ) {
            const parsed = normalizeUploadedFilesMeta(it.meta?.uploaded_files);
            if (parsed) {
              const k = getUploadItemKey(productId, context);
              window.uploadedFilesByItem[k] = parsed;
            }
          }
        });
      }
    }
  } catch (e) {
    console.warn("[estandefacil] Falha ao persistir metadados de upload:", e);
  }
}
const fileInputs = document.querySelectorAll('input[type="file"]');

fileInputs.forEach((input) => {
  input.addEventListener("change", async (event) => {
    if (window.isProgrammaticUpdate) {
      return;
    }
    toggleLocalLoader(true);
    const file = event.target.files[0];
    const folderBase = jQuery("#folder-name").val();
    const progressElement = jQuery("#image-progress");
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const timestamp = now.getTime();
    const folder = `${year}/${month}/${folderBase}`;
    const imageType = jQuery(input).data("image-type");

    if (file) {
      progressElement.removeClass("hidden").text("Carregando... 0%");
      try {
        const sanitizedFileName = file.name.replace(/\s+/g, "");
        const uniqueFileName = `${timestamp}_${sanitizedFileName}`;
        const formData = new FormData();
        const orderId = document.getElementById("order-id")?.value || "";
        formData.append("file", file);
        formData.append("folder", folder);
        if (orderId) formData.append("order_id", orderId);
        if (input.dataset.id) formData.append("product_id", input.dataset.id);
        if (input.dataset.context) formData.append("context", input.dataset.context);
        if (imageType) formData.append("image_type", imageType);
        if (input.name) formData.append("param", input.name);

        await jQuery.ajax({
          url: "/wp-admin/admin-ajax.php?action=upload_order_image",
          type: "POST",
          data: formData,
          contentType: false,
          processData: false,
          xhr: function () {
            const xhr = new window.XMLHttpRequest();
            xhr.upload.addEventListener(
              "progress",
              function (e) {
                if (e.lengthComputable) {
                  const percentComplete = Math.round(
                    (e.loaded / e.total) * 100
                  );
                  progressElement.text(`Carregando... ${percentComplete}%`);
                }
              },
              false
            );
            if (window.isProgrammaticUpdate) {
              return;
            }
            return xhr;
          },
          success: function (result) {
            if (result.success) {
              const fileOriginalUrl = result.data.url;
              const filePreviewUrl = result.data.preview_url;
              if (imageType) uploadedImages[imageType] = fileOriginalUrl;

              const name = input.name;
              const value = normalizeTextureUrl(filePreviewUrl || fileOriginalUrl);
              if (value) {
                updateSessionParameter({ name, value });
              } else {
                console.warn("URL inválida para textura:", filePreviewUrl);
              }
              const productId = input.dataset.id;
              const context = input.dataset.context || "";
              if (imageType && productId && !result?.data?.meta_saved) {
                const key = getUploadItemKey(productId, context);
                const current = window.uploadedFilesByItem[key] || {};
                current[imageType] = {
                  original: fileOriginalUrl,
                  preview: filePreviewUrl,
                  param: name,
                };
                window.uploadedFilesByItem[key] = current;
                persistUploadedFilesMeta(productId, context, current);
              }
              progressElement.text("Upload concluído!");
            } else {
              throw new Error(
                result.data.message || "Erro desconhecido no upload."
              );
            }
          },
          error: function () {
            progressElement.text("Erro no upload.");
            toggleLocalLoader(false);
          },
          complete: function () {
            setTimeout(() => {
              progressElement.addClass("hidden").text("");
            }, 2000);
          },
        });
      } catch (error) {
        console.error("Erro durante o upload:", error);
        progressElement.text("Erro no upload.");
        toggleLocalLoader(false);
      }
    } else {
      toggleLocalLoader(false);
    }
  });
});

if (document.getElementById("rotate-left")) {
  document
    .getElementById("rotate-left")
    .addEventListener("click", () => rotateModel(45, 0));

  document
    .getElementById("rotate-right")
    .addEventListener("click", () => rotateModel(-45, 0));

  document
    .getElementById("rotate-up")
    .addEventListener("click", () => rotateModel(0, 45));

  document
    .getElementById("rotate-down")
    .addEventListener("click", () => rotateModel(0, -45));
}

function rotateModel(yaw = 0, pitch = 0, duration = 250) {
  if (!viewport || !viewport.camera) return;
  let camera = viewport.camera;
  let position = [...camera.position];
  let target = [...camera.target];
  let dx = position[0] - target[0];
  let dy = position[1] - target[1];
  let dz = position[2] - target[2];
  let r = Math.sqrt(dx * dx + dy * dy + dz * dz);
  let theta = Math.atan2(dy, dx);
  let phi = Math.acos(dz / r);
  let targetTheta = theta + (yaw * Math.PI) / 180;
  let targetPhi = Math.max(
    0.1,
    Math.min(Math.PI / 2, phi + (pitch * Math.PI) / 180)
  );
  let startTime = null;

  function animate(time) {
    if (!startTime) startTime = time;
    let progress = (time - startTime) / duration;
    if (progress > 1) progress = 1;
    let newTheta = theta + (targetTheta - theta) * progress;
    let newPhi = phi + (targetPhi - phi) * progress;
    let newX = r * Math.sin(newPhi) * Math.cos(newTheta);
    let newY = r * Math.sin(newPhi) * Math.sin(newTheta);
    let newZ = r * Math.cos(newPhi);
    camera.position = [target[0] + newX, target[1] + newY, target[2] + newZ];
    viewport.update();
    if (progress < 1) requestAnimationFrame(animate);
  }
  requestAnimationFrame(animate);
}

async function fetchOrderDraftSummary(orderId) {
  if (!orderId) return null;
  try {
    if (
      typeof jQuery !== "undefined" &&
      typeof OrderDraftVars !== "undefined"
    ) {
      const resp = await jQuery.ajax({
        url: OrderDraftVars.ajax_url,
        method: "POST",
        data: {
          action: "get_user_draft_by_id",
          nonce: OrderDraftVars.nonce,
          order_id: orderId,
        },
        dataType: "json",
      });
      if (!resp) return null;
      if (resp.success && resp.data && resp.data.summary)
        return resp.data.summary;
      if (resp.success && resp.data) return resp.data;
      return null;
    }
    const body = new URLSearchParams();
    body.append("action", "get_user_draft_by_id");
    body.append(
      "nonce",
      typeof OrderDraftVars !== "undefined" ? OrderDraftVars.nonce : ""
    );
    body.append("order_id", orderId);
    const r = await fetch("/wp-admin/admin-ajax.php", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
    const json = await r.json();
    if (!json) return null;
    if (json.success && json.data && json.data.summary)
      return json.data.summary;
    if (json.success && json.data) return json.data;
    return null;
  } catch (err) {
    console.error("fetchOrderDraftSummary error", err);
    return null;
  }
}

function markInputsForItem(item) {
  const pid = item.product_id;
  const qty = parseInt(item.quantity, 10) || 0;
  const meta = item.meta || {};
  const context = meta.context || "";

  let qtySelector = `.quantity-input[data-id="${pid}"]`;
  if (context) qtySelector += `[data-context="${context}"]`;
  else qtySelector += `:not([data-context])`;

  const qtyInput = document.querySelector(qtySelector);
  if (qtyInput) {
    // console.log(
    //   "✅ Input Numérico encontrado:",
    //   qtySelector,
    //   "| Valor atual:",
    //   qtyInput.value,
    //   "| Novo valor:",
    //   qty
    // );
    if (parseInt(qtyInput.value) !== qty) {
      qtyInput.value = qty;
      setTimeout(() => {
        qtyInput.dispatchEvent(new Event("change", { bubbles: true }));
      }, 0);
    }
  } else {
    // Só loga aviso se a quantidade for > 0 (se for 0 e não achar, ok)
    if (qty > 0) {
      console.warn(
        "❌ Input Numérico NÃO encontrado para seletor:",
        qtySelector
      );
    }
  }

  let radioSelector = `input[type="radio"][data-id="${pid}"]`;
  if (context) radioSelector += `[data-context="${context}"]`;

  let valToMark = null;

  if (meta.hasOwnProperty("option_choice")) {
    valToMark = meta.option_choice;
  } else if (meta.hasOwnProperty("revestimento")) {
    valToMark = meta.revestimento;
  } else {
    // Lógica específica para SALAS (ID 76 ou radios estruturais)
    // Se o input tiver valor "2", precisamos garantir que ele seja alvo
    const radio2 = document.querySelector(`${radioSelector}[value="2"]`);
    if (radio2 && qty >= 2) valToMark = "2";
    else valToMark = qty > 0 ? "1" : "0";
  }

  if (valToMark !== null) {
    let targetSelector = `${radioSelector}[value="${valToMark}"]`;
    let radio = document.querySelector(targetSelector);

    // Fallback: Se tentou marcar algo específico e não achou, tenta "1" genérico
    if (!radio && qty > 0 && valToMark !== "0") {
      console.warn(
        "⚠️ Valor específico não achado, tentando fallback para '1'"
      );
      targetSelector = `${radioSelector}[value="1"]`;
      radio = document.querySelector(targetSelector);
    }

    if (radio) {
      // console.log(
      //   "✅ Radio encontrado:",
      //   targetSelector,
      //   "| Checked antes:",
      //   radio.checked
      // );
      if (!radio.checked) {
        radio.checked = true;
        setTimeout(() => {
          radio.dispatchEvent(new Event("change", { bubbles: true }));
        }, 0);
      }
    } else {
      const allRadios = document.querySelectorAll(radioSelector);
      // console.log(
      //   "   -> Radios disponíveis no DOM para este ID:",
      //   allRadios.length,
      //   allRadios
      // );
    }
  }
  console.groupEnd();
}

async function populateUIFromSummary(summary) {
  if (!summary) return;

  try {
    const items = Array.isArray(summary.items) ? summary.items : [];

    items.forEach((item) => {
      markInputsForItem(item);
    });

    const byPid = items.reduce((acc, it) => {
      (acc[it.product_id] ||= []).push(it);
      return acc;
    }, {});

    const qtyOf = (pid) =>
      (byPid[pid] || []).reduce(
        (s, it) => s + (parseInt(it.quantity, 10) || 0),
        0
      );
    const findCtx = (pid, ctx) =>
      (byPid[pid] || []).find((it) => it.meta && it.meta.context === ctx);
    const qtyCtx = (pid, ctx) =>
      (byPid[pid] || [])
        .filter((it) => it.meta && it.meta.context === ctx)
        .reduce((s, it) => s + (parseInt(it.quantity, 10) || 0), 0);

    const SALA_IDS = [76];
    let salaCount = 0;
    for (const sid of SALA_IDS)
      salaCount = Math.max(salaCount, Math.min(2, qtyOf(sid)));

    if (salaCount === 0) {
      const hasS1 = items.some(
        (it) => it.meta?.context === "sala_1" && it.quantity > 0
      );
      const hasS2 = items.some(
        (it) => it.meta?.context === "sala_2" && it.quantity > 0
      );
      salaCount = hasS1 && hasS2 ? 2 : hasS1 || hasS2 ? 1 : 0;
    }

    beginBatch();

    await updateSessionParameter({ name: "sala", value: String(salaCount) });

    const depositoOn = qtyOf(74) > 0 || qtyOf(75) > 0;
    await updateSessionParameter({
      name: "377c77ec-90e8-4a14-902b-8444535bfcb6",
      value: depositoOn ? "1" : "0",
    });

    const quadroItems = items.filter((it) => it.product_id === 85);
    const quadroOn = quadroItems.some(
      (it) => (parseInt(it.quantity, 10) || 0) > 0
    );
    const quadroVal = quadroOn ? "1" : "0";
    await updateSessionParameter({
      name: "68060fa1-144d-46a0-9f16-1e04e78f3e78",
      value: quadroVal,
    });
    const quadroToggleItem = quadroItems.find(
      (it) =>
        it.meta &&
        it.meta.quadro_toggle !== undefined &&
        it.meta.quadro_toggle !== ""
    );
    await updateSessionParameter({
      name: "817ceb83-0ff2-4bf5-99e0-9e28a89ac110",
      value: quadroToggleItem
        ? String(quadroToggleItem.meta.quadro_toggle)
        : "0",
    });

    const balcaoHallOn =
      !!findCtx(79, "hall") ||
      (qtyOf(79) > 0 && !findCtx(79, "sala_1") && !findCtx(79, "sala_2"));
    await updateSessionParameter({
      name: "3155ea16-7a8f-4d29-b2b8-97523bd51a9a",
      value: balcaoHallOn ? "1" : "0",
    });
    await updateSessionParameter({
      name: "682e8931-33e5-4179-8952-f9bf2e01e5c3",
      value: String(qtyCtx(77, "hall")),
    });
    await updateSessionParameter({
      name: "3b3ea4bc-02bf-4c28-b5f2-b7da7956964d",
      value: String(qtyCtx(78, "hall")),
    });
    await updateSessionParameter({
      name: "04b23cbc-80fa-42ef-854f-8f4e541e962a",
      value: String(qtyCtx(83, "hall")),
    });

    await updateSessionParameter({
      name: "78b052ec-acd6-411b-8ab8-8c37559d4956",
      value: !!findCtx(77, "sala_1") ? "1" : "0",
    });
    await updateSessionParameter({
      name: "8398b103-19eb-47e7-8aab-58473fd1b331",
      value: !!findCtx(77, "sala_2") ? "1" : "0",
    });
    await updateSessionParameter({
      name: "c55fac73-147d-499a-9e1c-b8af716635d4",
      value: !!findCtx(79, "sala_1") ? "1" : "0",
    });
    await updateSessionParameter({
      name: "8e0c914d-e5fd-4ade-91eb-51c10fbe266f",
      value: !!findCtx(79, "sala_2") ? "1" : "0",
    });

    const fS1 = findCtx(82, "sala_1");
    const fS2 = findCtx(82, "sala_2");
    const fDep = findCtx(82, "deposito");

    await updateSessionParameter({
      name: "98d8dd37-c7ff-41ef-85ad-7f7ecc11c96a",
      value: fS1 && fS1.quantity > 0 ? "1" : "0",
    });
    await updateSessionParameter({
      name: "7254a11c-99ce-4986-9300-6a311fc5e830",
      value: fS2 && fS2.quantity > 0 ? "1" : "0",
    });
    await updateSessionParameter({
      name: "88112e7a-4b58-4002-b760-2d8b003e2fd9",
      value: fDep && fDep.quantity > 0 ? "1" : "2",
    });

    const revestItem = summary.items.find(
      (it) =>
        it.product_id === 86 && (it.meta?.revestimento || it.meta?.color_hex)
    );
    if (revestItem) {
      const val =
        revestItem.meta.color_hex || revestItem.meta.revestimento || "#ffffff";
      const pid =
        revestItem.meta.param_id || "22ff4752-5564-492a-ae84-1d58db987dfc";
      await updateSessionParameter({ name: pid, value: val });
    }

    await endBatch();
    await applyUploadedFilesFromSummary(summary);

    try {
      window.dispatchEvent(
        new CustomEvent("draft:summary_applied", { detail: { summary } })
      );
    } catch (e) {
      console.warn("[estandefacil] Falha ao disparar evento draft:summary_applied:", e);
    }
  } catch (err) {
    console.error("populateUIFromSummary error", err);
  }
}

window.getTopDownScreenshot = async function () {
  if (
    !window.session ||
    !window.viewport ||
    !window.topCamera ||
    !window.perspectiveCamera
  ) {
    return null;
  }

  toggleLocalLoader(true);
  const params = session.parameters;
  const originalValues = {};

  const hideKeywords = [
    "cobertura",
    "teto",
    "roof",
    "ceiling",
    "grama",
    "grass",
    "terreno",
    "ground",
    "chao",
    "piso",
    "sky",
    "ceu",
    "environment",
  ];

  try {
    for (const key in params) {
      const p = params[key];
      const pName = p.name ? p.name.toLowerCase() : "";
      originalValues[key] = p.value;

      if (p.id === "ca0cf7c5-0bcd-4e9b-aabc-d2e3e97f9134") {
        p.value = "1";
      }
      // Para os demais itens genéricos (grama, chão), mantemos "0" (oculto/off)
      else if (hideKeywords.some((k) => pName.includes(k))) {
        if (typeof p.value === "string") p.value = "0";
        else if (typeof p.value === "number") p.value = 0;
      }
    }

    await session.customize();
    await new Promise((r) => setTimeout(r, 300));

    viewport.assignCamera(topCamera.id);

    if (topCamera) {
      topCamera.position = [0, 0, 1000];
      topCamera.target = [0, 0, 0];
    }

    // Força Zoom na câmera ortográfica para preencher a imagem
    if (viewport.camera) {
      viewport.camera.zoom = 2.0; // Aumente se ficar longe, diminua se cortar
    }

    viewport.update();

    await new Promise((r) => setTimeout(r, 400));

    const screenshot = await viewport.getScreenshot("png", 1000, 1000);

    if (!screenshot || !screenshot.startsWith("data:image")) {
      throw new Error("Screenshot gerado inválido.");
    }

    return screenshot;
  } catch (error) {
    return null;
  } finally {
    try {
      for (const key in originalValues) {
        if (session.parameters[key]) {
          session.parameters[key].value = originalValues[key];
        }
      }
      await session.customize();
    } catch (err) {
      console.warn("⚠️ Falha ao restaurar parâmetros:", err);
    }

    viewport.assignCamera(perspectiveCamera.id);
    if (
      viewport.controls &&
      typeof viewport.controls.fitToScene === "function"
    ) {
      viewport.controls.fitToScene();
    }
    toggleLocalLoader(false);
  }
};

document.addEventListener("DOMContentLoaded", () => {
  const targetModal = document.querySelector(
    '.modal[data-target="planta-eletrica"]'
  );
  const globalLoader = document.querySelector('.modal[data-target="loader"]'); // Seleciona o loader que trava a tela

  if (!targetModal) return;

  const observer = new MutationObserver(async (mutations) => {
    for (const mutation of mutations) {
      if (
        mutation.attributeName === "class" &&
        targetModal.classList.contains("active")
      ) {
        // console.log(
        //   "📸 Modal da planta elétrica aberto — gerando screenshots..."
        // );
        try {
          // 1. ATIVA O LOADER GLOBAL (Trava a tela)
          if (globalLoader) globalLoader.classList.add("active");

          if (!window.session || !window.viewport) {
            await new Promise((resolve) => {
              const checkInterval = setInterval(() => {
                if (window.session && window.viewport) {
                  clearInterval(checkInterval);
                  resolve();
                }
              }, 300);
            });
          }

          const session = window.session;
          const params = session?.parameters || {};
          const originalValues = {};
          for (const key in params) originalValues[key] = params[key].value;

          // --- PLANTA FINAL MONTADA ---
          Object.keys(params).forEach((id) => {
            const p = params[id];
            // Oculta Chão/Piso genericamente
            if (p.name.toLowerCase().match(/chao|piso|floor|ground|textura/))
              p.value = "0";
            // Oculta TETO explicitamente (ID específico = "1")
            if (p.id === "ca0cf7c5-0bcd-4e9b-aabc-d2e3e97f9134") p.value = "1";
          });
          await session.customize();
          const screenshotFinal = await window.getTopDownScreenshot();

          // --- PLANTA BASE (Contorno) ---
          Object.keys(params).forEach((id) => {
            const p = params[id];
            // Remove itens internos para deixar só paredes
            if (typeof p.value === "string") {
              if (
                p.name
                  .toLowerCase()
                  .match(
                    /sala|parede|objeto|teto|textura|móvel|balcão|bancada|mesa|bistrô|iluminação|forro|acabamento|chao|piso/
                  )
              ) {
                p.value = "0";
              }
            }
            // Remove teto aqui também
            if (p.id === "ca0cf7c5-0bcd-4e9b-aabc-d2e3e97f9134") p.value = "1";
          });
          await session.customize();
          const screenshotBase = await window.getTopDownScreenshot();

          // Restaura tudo
          Object.keys(originalValues).forEach((id) => {
            params[id].value = originalValues[id];
          });
          await session.customize();

          if (screenshotFinal) {
            const el = document.querySelector("#planta-eletrica-container");
            if (el)
              el.innerHTML = `<div class="planta-screenshot"><img src="${screenshotFinal}" style="width:100%;border:1px solid #ccc;border-radius:8px;margin-top:10px;"></div>`;
          }
          if (screenshotBase) {
            const el = document.querySelector("#planta-base-container");
            if (el)
              el.innerHTML = `<div class="planta-screenshot"><img src="${screenshotBase}" style="width:100%;border:1px solid #ccc;border-radius:8px;margin-top:10px;"></div>`;
          }
        } catch (error) {
          console.error("❌ Erro ao gerar plantas:", error);
        } finally {
          // 2. REMOVE O LOADER GLOBAL (Destrava a tela)
          if (globalLoader) globalLoader.classList.remove("active");
          toggleLocalLoader(false);
        }
      }
    }
  });
  observer.observe(targetModal, { attributes: true });
});
